import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useOptionalAviosAuth } from '@channel/shared-header-footer/auth-client'
import { useAuthContext, useAviosAuthEnabled, useIsAuthRequiredForPath } from '~/context/auth'
import { useFeatureFlagWithMultipleVariables } from '~/context/featureFlag'
import { useLocalisation } from '~/context/localisation'
import { usePlatformConfig } from '~/context/platform'
import { useContentfulPreviewMode } from '~/hooks/useContentfulPreviewMode/useContentfulPreviewMode'
import { getEducationalComponent } from '~/services/contentService'
import { isClient } from '~/utils/envChecks'
import { isNotEmpty } from '~/utils/typeChecks'
import {
  SIGN_IN_BENEFITS_CONTENTFUL_IDENTIFIER,
  SignInBenefit,
  getOpenDelayMs,
  getShowAgainAfterMs,
  hasSeenSignInBenefitsModal,
  mapEducationalItemToBenefit,
  markSignInBenefitsModalSeen,
} from './signInBenefitsModal.utils'

export type SignInBenefitsModalState = {
  isOpen: boolean
  heading?: string
  benefits: SignInBenefit[]
  close: () => void
  signIn: () => void
}

export const useSignInBenefitsModal = (): SignInBenefitsModalState => {
  const router = useRouter()
  const { opCo } = usePlatformConfig()
  const { locale } = useLocalisation()
  const preview = useContentfulPreviewMode()
  const { value: isFeatureEnabled, variables: featureVariables } =
    useFeatureFlagWithMultipleVariables('sign-in-benefits-modal')
  const openDelayMs = getOpenDelayMs(featureVariables['delay-seconds'])
  const showAgainAfterMs = getShowAgainAfterMs(featureVariables['show-again-after-hours'])
  const { isAuthenticated, isLoading: isAuthLoading, handleLogin } = useAuthContext()
  const aviosAuthEnabled = useAviosAuthEnabled()
  const aviosAuth = useOptionalAviosAuth()
  const isAuthRequired = useIsAuthRequiredForPath()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  // Treat the modal as seen until the cookie has been read on the client
  const [hasBeenSeen, setHasBeenSeen] = useState(true)

  // Read again on every page, as the layout can stay mounted for longer than the show again time
  useEffect(() => {
    setHasBeenSeen(hasSeenSignInBenefitsModal(showAgainAfterMs))
  }, [showAgainAfterMs, router.pathname])

  // Every page an unauthenticated user can browse, on the others they are being redirected to login
  const isEligible =
    isFeatureEnabled && !isAuthRequired && !isAuthLoading && !isAuthenticated && !hasBeenSeen

  const { data: content } = useQuery({
    queryKey: ['signInBenefits', opCo, SIGN_IN_BENEFITS_CONTENTFUL_IDENTIFIER, locale, preview],
    queryFn: () =>
      getEducationalComponent(opCo, SIGN_IN_BENEFITS_CONTENTFUL_IDENTIFIER, locale, preview),
    enabled: isEligible,
  })

  const benefits = useMemo(
    () => content?.items?.map(mapEducationalItemToBenefit).filter(isNotEmpty) ?? [],
    [content?.items]
  )
  // The heading also names the dialog for assistive technology, so both are required
  const hasContent = !!content?.heading && benefits.length > 0

  // Open once the page has loaded: the document and its images (the window load event, already
  // fired after client side navigation) and none of its data still being fetched, then after the
  // delay from the feature flag.
  // The query cache is only watched while the modal is ready to open, rather than re-rendering the
  // layout on every request for all users
  useEffect(() => {
    if (!isEligible || !hasContent) {
      return undefined
    }

    let openTimeout: ReturnType<typeof setTimeout> | undefined
    const openWhenPageLoaded = () => {
      const isDocumentLoaded = isClient() && document.readyState === 'complete'
      if (openTimeout !== undefined || !isDocumentLoaded || queryClient.isFetching() > 0) {
        return
      }
      // The delay runs from the page being ready. It is only marked as seen once it is shown,
      // so leaving the page while waiting does not use up the once per session
      openTimeout = setTimeout(() => {
        setHasBeenSeen(true)
        // Another tab may have shown it while this one was waiting
        if (!hasSeenSignInBenefitsModal(showAgainAfterMs)) {
          markSignInBenefitsModalSeen()
          setIsOpen(true)
        }
      }, openDelayMs)
    }

    openWhenPageLoaded()
    const unsubscribe = queryClient.getQueryCache().subscribe(openWhenPageLoaded)
    if (isClient()) {
      window.addEventListener('load', openWhenPageLoaded)
    }

    return () => {
      unsubscribe()
      clearTimeout(openTimeout)
      if (isClient()) {
        window.removeEventListener('load', openWhenPageLoaded)
      }
    }
  }, [isEligible, hasContent, openDelayMs, showAgainAfterMs, queryClient])

  // Leaving the page, e.g. with the browser Back button, closes it. Shallow route changes are the
  // results page updating its own URL
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const closeOnRouteChange = (_url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow) {
        setIsOpen(false)
      }
    }

    router.events.on('routeChangeStart', closeOnRouteChange)
    return () => {
      router.events.off('routeChangeStart', closeOnRouteChange)
    }
  }, [isOpen, router.events])

  const close = useCallback(() => setIsOpen(false), [])

  const signIn = useCallback(() => {
    if (aviosAuthEnabled) {
      aviosAuth?.login({ opcoId: opCo })
      return
    }

    handleLogin({ returnToPath: router.asPath })
  }, [aviosAuth, aviosAuthEnabled, handleLogin, opCo, router.asPath])

  return {
    isOpen,
    heading: content?.heading,
    benefits,
    close,
    signIn,
  }
}
